import { Component, Show, createMemo, createSignal } from 'solid-js'
import { Toggle } from '../../../shared/Toggle'
import Tabs from '../../../shared/Tabs'
import ElevenLabsSettings from './ElevenLabsSettings'
import WebSpeechSynthesisSettings from './WebSpeechSynthesisSettings'
import NovelTtsSettings from './NovelTtsSettings'
import Divider from '../../../shared/Divider'
import { getSpeechRecognition } from '../../Chat/components/SpeechRecognitionRecorder'
import HuanYouAISettings from './AgnaisticSettings'
import { isHuanYouAISpeechAllowed } from '/web/shared/Audio/speech'
import { SetStoreFunction } from 'solid-js/store'
import { applyStoreProperty } from '/web/shared/util'
import { UserSettings } from '../util'
import { i18nStore } from '../../../store'

export const VoiceSettings: Component<{
  state: UserSettings
  setter: SetStoreFunction<UserSettings>
}> = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))

  const ttsServiceTabs = {
    agnaistic: i18n.t.settings_voice_agnaistic,
    webspeechsynthesis: i18n.t.settings_voice_webspeechsynthesis,
    elevenlabs: i18n.t.settings_voice_elevenlabs,
    novel: i18n.t.settings_voice_novel,
  }

  type Tab = keyof typeof ttsServiceTabs

  const canUseTts = createMemo(() => {
    return isHuanYouAISpeechAllowed()
  })

  const [tab, setTab] = createSignal(0)

  const tabs = createMemo(() => {
    const opts: Tab[] = ['webspeechsynthesis', 'elevenlabs', 'novel']
    if (canUseTts()) {
      opts.unshift('agnaistic')
    }
    return opts
  })

  const currentTab = createMemo(() => tabs()[tab()])
  const subclass = 'flex flex-col gap-4'

  return (
    <>
      <div class="flex flex-col gap-4">
        <p class="text-lg font-bold">{i18n.t.settings_voice_sttTitle}</p>

        <Show
          when={getSpeechRecognition()}
          fallback={<p class="text-red-800">{i18n.t.settings_voice_sttNotAvailable}</p>}
        >
          <p class="italic">
            {i18n.t.settings_voice_sttHelper}
          </p>

          <Toggle
            label={i18n.t.settings_voice_sttEnabled}
            helperText={i18n.t.settings_voice_sttEnabledHelper}
            fieldName="speechToTextEnabled"
            value={props.state.speechtotext?.enabled ?? true}
            onChange={(ev) =>
              props.setter(
                'speechtotext',
                applyStoreProperty(props.state.speechtotext, 'enabled', ev)
              )
            }
          />

          <Toggle
            label={i18n.t.settings_voice_sttAutoSubmit}
            helperText={i18n.t.settings_voice_sttAutoSubmitHelper}
            fieldName="speechToTextAutoSubmit"
            value={props.state.speechtotext?.autoSubmit ?? true}
            onChange={(ev) =>
              props.setter(
                'speechtotext',
                applyStoreProperty(props.state.speechtotext, 'autoSubmit', ev)
              )
            }
          />

          <Toggle
            label={i18n.t.settings_voice_sttAutoRecord}
            helperText={i18n.t.settings_voice_sttAutoRecordHelper}
            fieldName="speechToTextAutoRecord"
            value={props.state.speechtotext?.autoRecord ?? true}
            onChange={(ev) =>
              props.setter(
                'speechtotext',
                applyStoreProperty(props.state.speechtotext, 'autoRecord', ev)
              )
            }
          />
        </Show>

        <Divider />

        <p class="text-lg font-bold">{i18n.t.settings_voice_ttsTitle}</p>

        <p class="italic">{i18n.t.settings_voice_ttsHelper}</p>

        <Toggle
          label={i18n.t.settings_voice_ttsEnabled}
          helperText={i18n.t.settings_voice_ttsEnabledHelper}
          fieldName="textToSpeechEnabled"
          value={props.state.texttospeech?.enabled ?? true}
          onChange={(ev) =>
            props.setter(
              'texttospeech',
              applyStoreProperty(props.state.texttospeech, 'enabled', ev)
            )
          }
        />

        <Toggle
          label={i18n.t.settings_voice_ttsFilterActions}
          helperText={i18n.t.settings_voice_ttsFilterActionsHelper}
          fieldName="textToSpeechFilterActions"
          value={props.state.texttospeech?.filterActions ?? true}
          onChange={(ev) =>
            props.setter(
              'texttospeech',
              applyStoreProperty(props.state.texttospeech, 'filterActions', ev)
            )
          }
        />

        <Divider />

        <p class="text-lg font-bold">{i18n.t.settings_voice_voiceServices}</p>

        <div class="my-2">
          <Tabs tabs={tabs().map((t) => ttsServiceTabs[t])} selected={tab} select={setTab} />
        </div>

        <div class="flex flex-col gap-4">
          <div class={currentTab() === 'agnaistic' ? subclass : 'hidden'}>
            <HuanYouAISettings />
          </div>

          <div class={currentTab() === 'webspeechsynthesis' ? subclass : 'hidden'}>
            <WebSpeechSynthesisSettings />
          </div>

          <div class={currentTab() === 'elevenlabs' ? subclass : 'hidden'}>
            <ElevenLabsSettings state={props.state} setter={props.setter} />
          </div>

          <div class={currentTab() === 'novel' ? subclass : 'hidden'}>
            <NovelTtsSettings />
          </div>
        </div>
      </div>
    </>
  )
}
